import {noop} from 'es-toolkit'
import {useFormik} from 'formik'
import invariant from 'invariant'

import {useAppDispatch, useAppSelector} from '../../../hooks/redux'
import {closeModal} from '../../../redux/features/modal'

import preventDoubleClick from '../../../../../commons/src/services/prevent-double-click'
import * as optionApi from '../../../services/api/option'
import {
  formDataToPartialProductionTimeOptionRequest,
  productionTimeOptionsToFormData,
} from '../../forms/production-times/utils'
import {toTouchedFormData} from '../../utils/form'

import {DefaultProductionTimes, ModalMeta, OptionWithLinkedProviderInfos} from '../../../types'

import Button from '../../../../../commons/src/components/button'
import Headline from '../../../../../commons/src/components/headline'
import Modal from '../../../../../commons/src/components/modal'

import ProductionTimesForm, {
  MixedFormData,
  SingleFormData,
  createValidationSchemaWithDefaultProductionTimes,
  initialValues,
} from '../../forms/production-times/form'

type Props = {
  meta: ModalMeta
  defaultProductionTimes: DefaultProductionTimes
  productionTimeOptions: OptionWithLinkedProviderInfos[]
  linkedConfigsCount: number
  onDone?: () => void
}

const EditProductionTimesModal = ({
  meta,
  productionTimeOptions = [],
  defaultProductionTimes,
  linkedConfigsCount,
  onDone = noop,
}: Props) => {
  const dispatch = useAppDispatch()
  const provider = useAppSelector(state => state.core.provider)
  invariant(provider, 'Provider is missing.')

  const editMultiple = productionTimeOptions.length > 1

  const formik = useFormik<MixedFormData>({
    initialValues: {
      ...initialValues,
      ...productionTimeOptionsToFormData(productionTimeOptions),
    },
    validationSchema: createValidationSchemaWithDefaultProductionTimes(defaultProductionTimes),
    onSubmit: async (values, actions) => {
      const touchedValues = toTouchedFormData(
        values,
        formik.initialValues
      ) as Partial<SingleFormData>
      const productionTimeOptionRequest =
        formDataToPartialProductionTimeOptionRequest(touchedValues)

      try {
        const productionTimeOptionIds = productionTimeOptions.map(({id}) => id)
        await optionApi.patchMultiple(productionTimeOptionRequest, productionTimeOptionIds)

        onDone()
        dispatch(closeModal())
      } catch (error) {
        actions.setFieldError('formikGeneral', (error as Error).message)
      }
    },
  })

  return (
    <Modal
      fullscreenMobile={meta.fullscreenMobile}
      headline={
        <Headline
          label="Edit production time options"
          addendum={editMultiple && `(${productionTimeOptions.length} items)`}
          size="l"
        />
      }
      onClose={() => dispatch(closeModal())}
      buttons={
        <>
          <Button hierarchy="ghost" onClick={() => dispatch(closeModal())}>
            Cancel
          </Button>
          <Button
            disabled={
              formik.isSubmitting ||
              (formik.submitCount > 0 && Object.keys(formik.errors).length > 0)
            }
            onClick={preventDoubleClick(() => formik.handleSubmit())}
          >
            Save
          </Button>
        </>
      }
    >
      <ProductionTimesForm
        formik={formik}
        provider={provider}
        linkedConfigsCount={linkedConfigsCount}
      />
    </Modal>
  )
}

export default EditProductionTimesModal
